import type { Currency } from 'dinero.js'
import Dinero from 'dinero.js'

import countryCurrencyMapping from 'modules/core/lib/utils/countryCurrencyMapping.json'

interface Options {
  amount: number
  currency?: Currency | string | null
  locale: string
  optionalDecimals?: boolean
  emptyValue?: string
}

const currencySet: Set<string> = new Set([
  'AED',
  'AFN',
  'ALL',
  'AMD',
  'ANG',
  'AOA',
  'ARS',
  'AUD',
  'AWG',
  'AZN',
  'BAM',
  'BBD',
  'BDT',
  'BGN',
  'BHD',
  'BIF',
  'BMD',
  'BND',
  'BOB',
  'BOV',
  'BRL',
  'BSD',
  'BTN',
  'BWP',
  'BYN',
  'BZD',
  'CAD',
  'CDF',
  'CHE',
  'CHF',
  'CHW',
  'CLF',
  'CLP',
  'CNY',
  'COP',
  'COU',
  'CRC',
  'CUC',
  'CUP',
  'CVE',
  'CZK',
  'DJF',
  'DKK',
  'DOP',
  'DZD',
  'EGP',
  'ERN',
  'ETB',
  'EUR',
  'FJD',
  'FKP',
  'GBP',
  'GEL',
  'GHS',
  'GIP',
  'GMD',
  'GNF',
  'GTQ',
  'GYD',
  'HKD',
  'HNL',
  'HRK',
  'HTG',
  'HUF',
  'IDR',
  'ILS',
  'INR',
  'IQD',
  'IRR',
  'ISK',
  'JMD',
  'JOD',
  'JPY',
  'KES',
  'KGS',
  'KHR',
  'KMF',
  'KPW',
  'KRW',
  'KWD',
  'KYD',
  'KZT',
  'LAK',
  'LBP',
  'LKR',
  'LRD',
  'LSL',
  'LYD',
  'MAD',
  'MDL',
  'MGA',
  'MKD',
  'MMK',
  'MNT',
  'MOP',
  'MRU',
  'MUR',
  'MVR',
  'MWK',
  'MXN',
  'MXV',
  'MYR',
  'MZN',
  'NAD',
  'NGN',
  'NIO',
  'NOK',
  'NPR',
  'NZD',
  'OMR',
  'PAB',
  'PEN',
  'PGK',
  'PHP',
  'PKR',
  'PLN',
  'PYG',
  'QAR',
  'RON',
  'RSD',
  'RUB',
  'RWF',
  'SAR',
  'SBD',
  'SCR',
  'SDG',
  'SEK',
  'SGD',
  'SHP',
  'SLL',
  'SOS',
  'SRD',
  'SSP',
  'STN',
  'SVC',
  'SYP',
  'SZL',
  'THB',
  'TJS',
  'TMT',
  'TND',
  'TOP',
  'TRY',
  'TTD',
  'TWD',
  'TZS',
  'UAH',
  'UGX',
  'USD',
  'USN',
  'UYI',
  'UYU',
  'UYW',
  'UZS',
  'VES',
  'VND',
  'VUV',
  'WST',
  'XAF',
  'XAG',
  'XAU',
  'XBA',
  'XBB',
  'XBC',
  'XBD',
  'XCD',
  'XDR',
  'XOF',
  'XPD',
  'XPF',
  'XPT',
  'XSU',
  'XTS',
  'XUA',
  'XXX',
  'YER',
  'ZAR',
  'ZMW',
  'ZWL',
])

export function isCurrency(currency?: string): currency is Currency {
  return !!currency && currencySet.has(currency.toUpperCase())
}

export default function MoneyWithCurrency(options: Options): string {
  const defaultFormat = '$0,0.00'
  const noDecimalsFormat = '$0,0'

  if (!options.currency || !isCurrency(options.currency)) {
    return options.emptyValue ?? ''
  }

  const money = Dinero({
    amount: options.amount,
    currency: options.currency,
  }).setLocale(options.locale)

  if (options.optionalDecimals) {
    const format = money.hasSubUnits() ? defaultFormat : noDecimalsFormat
    return money.toFormat(format)
  }
  return money.toFormat(defaultFormat)
}

interface CountryCurrencyPair {
  country: string
  currency: string
}

/**
 * Returns the currency code for a given country code.
 *
 * @param {string} countryCode - The ISO 3166-1 alpha-2 country code.
 * @returns {string | undefined} The ISO 4217 currency code for the country, or undefined if the country code is not found.
 */
export function getCurrencyCode(countryCode: string): string | undefined {
  const pair = countryCurrencyMapping.find(
    (item: CountryCurrencyPair) => item.country === countryCode
  )
  return pair ? pair.currency : undefined
}
